/**
 * Wave 2 leave hooks: the single-request read and the paged request list.
 *
 * useLeaveRequest — GET /leave/requests/{id}. Replaces deriving the detail
 * row from the caller's role-scoped LIST (useLeaveRequestFromList), which
 * showed "Request not found" to anyone the list did not happen to include
 * — e.g. a team lead opening a report's request from a notification. The
 * server authorises the read (owner / HR / approver chain / live
 * out-of-office delegate / decider) and also says what the caller may do
 * (can_decide, can_self_approve) and who the request is waiting on.
 *
 * useLeaveRequestPages — the leave list in pages of LEAVE_PAGE_SIZE
 * (?limit/&offset; the server adds next_offset). Keys live under the
 * ["leave"] root, so every leave mutation's invalidateQueries(["leave"])
 * refreshes them.
 */

import axios from "axios";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { leaveApi } from "@/lib/api";
import { leaveKeys } from "@/hooks/use-leave";

export const leaveRequestKey = (id: number) =>
  [...leaveKeys.all, "request", id] as const;

function isNotFound(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 404;
}

export function useLeaveRequest(id: number) {
  return useQuery({
    queryKey: leaveRequestKey(id),
    queryFn: ({ signal }) => leaveApi.getLeaveRequest(id, signal),
    enabled: Number.isFinite(id) && id > 0,
    // A 404 is a definitive answer (missing or not visible) — don't retry.
    retry: (count, err) => !isNotFound(err) && count < 1,
  });
}

export { isNotFound as isLeaveNotFound };

export const LEAVE_PAGE_SIZE = 50;

export interface LeaveListParams {
  status?: string;
  scope?: "self";
  exclude_self?: boolean;
  mine?: boolean;
}

/**
 * Query options for the paged leave list — shared by the hook and the
 * route loaders (prefetchInfiniteQuery) so both hit the same cache key.
 * `view` mirrors leaveKeys.requests' discriminator.
 */
export function leavePagesQueryOptions(
  params: LeaveListParams,
  view: "excl-self" | "mine" | undefined,
) {
  return infiniteQueryOptions({
    queryKey: [
      ...leaveKeys.requests(params.status, view),
      params.scope ?? "scoped",
      "paged",
    ],
    queryFn: ({ pageParam, signal }) =>
      leaveApi.listLeaveRequests(
        { ...params, limit: LEAVE_PAGE_SIZE, offset: pageParam },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
  });
}

/** Paged leave list (see leavePagesQueryOptions). */
export function useLeaveRequestPages(
  params: LeaveListParams,
  view: "excl-self" | "mine" | undefined,
) {
  return useInfiniteQuery(leavePagesQueryOptions(params, view));
}
